import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import useGlobalContext from "@hooks/common/general/use-global-context";
import { readReplayHintClickCount, recordLocalReplayHintClick, REPLAY_DISCOVERY_CLICK_LIMIT } from "@Context/game/activity/replay-discovery-storage";

interface IReplayDiscoveryContext {
  showReplayHint: boolean;
  dismissReplayHint: () => void;
}

const ReplayDiscoveryContext = createContext<IReplayDiscoveryContext | null>(null);

export const useReplayDiscovery = () => {
  const context = useContext(ReplayDiscoveryContext);
  if (!context) {
    throw new Error("useReplayDiscovery must be used within ReplayDiscoveryProvider");
  }
  return context;
};

const UserReplayDiscoveryProvider = ({ children, userId }: { children: ReactNode; userId: string | null }) => {
  const [count] = useState(() => (userId ? readReplayHintClickCount(userId) : 0));
  const [hasClicked, setHasClicked] = useState(false);
  const clickedRef = useRef(false);

  const dismissReplayHint = useCallback(() => {
    if (!userId || clickedRef.current || count >= REPLAY_DISCOVERY_CLICK_LIMIT) return;
    clickedRef.current = true;
    setHasClicked(true);
    recordLocalReplayHintClick(userId, count);
  }, [count, userId]);

  const value = useMemo(
    () => ({ showReplayHint: Boolean(userId) && !hasClicked && count < REPLAY_DISCOVERY_CLICK_LIMIT, dismissReplayHint }),
    [count, dismissReplayHint, hasClicked, userId]
  );

  return <ReplayDiscoveryContext.Provider value={value}>{children}</ReplayDiscoveryContext.Provider>;
};

export const ReplayDiscoveryProvider = ({ children }: { children: ReactNode }) => {
  const { globalState } = useGlobalContext();
  const userId = globalState.user.authenticated ? globalState.user.id : null;
  return (
    <UserReplayDiscoveryProvider key={userId} userId={userId}>
      {children}
    </UserReplayDiscoveryProvider>
  );
};
